/**
 * Walk-in — a single drop-in session at one flat price for any class.
 *
 * Fast, but not anonymous: name and phone are REQUIRED. A drop-in is still a
 * person on the mat, and the academy needs to know who was in the building.
 * No member row and no SK ID is created — see WalkIn in types.ts.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { getWalkInPrice, recordWalkIn, validateWalkIn } from '../db/operations';
import { filterWalkInRowsByRange, loadWalkInHistory } from '../db/queries';
import ClassPicker from '../components/ClassPicker';
import VoidWalkInDialog from '../components/VoidWalkInDialog';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { formatDisplayDate, todayISO } from '@shared/dates';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, type PaymentMethod } from '@shared/types';
import type { WalkIn as WalkInRow } from '@shared/types';

export default function WalkIn() {
  const [classKey, setClassKey] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [personName, setPersonName] = useState('');
  const [phone, setPhone] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState<WalkInRow | null>(null);
  const [voidingFor, setVoidingFor] = useState<WalkInRow | null>(null);

  // Reactive so a void here — or a walk-in synced in from another device —
  // updates the list without a reload. Scoped to TODAY: this is a glance at
  // what has just happened at the desk, not a record — that's /walk-ins.
  const today = todayISO();
  const history = useLiveQuery(() => loadWalkInHistory(), [], undefined);
  const recent = useMemo(
    () => (history ? filterWalkInRowsByRange(history, today, today) : undefined),
    [history, today]
  );

  /**
   * The walk-in rate, and the ONLY price the front desk can charge.
   *
   * It used to be editable here, per visit. It is not any more: a rate the desk
   * can change on the spot is a rate nobody can reconcile against the till, and
   * the payout report has no way to tell a discount from a mistake. Changing it
   * is a manager decision, made in Settings (behind the manager PIN) or in the
   * management portal, and it then reaches every device on the next sync.
   *
   * Kept live rather than read once, so a change made in the portal appears
   * here as soon as it syncs instead of on the next reload.
   */
  useEffect(() => {
    getWalkInPrice().then(setAmount);
  }, []);

  const price = amount ?? 0;

  const errors = validateWalkIn({ classKey, personName, phone });
  const canSave = Object.keys(errors).length === 0;

  async function save() {
    setTouched(true);
    if (!canSave) {
      setError(Object.values(errors)[0] ?? '');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const row = await recordWalkIn({
        classKey,
        personName,
        phone,
        amountPiastres: price,
        method,
        receivedBy: receivedBy.trim() || 'Front desk',
      });
      setDone(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setClassKey('');
    setPersonName('');
    setPhone('');
    setDone(null);
    setError('');
    setTouched(false);
    setAmount(await getWalkInPrice());
  }

  if (done) {
    return (
      <div className="mx-auto max-w-md px-5 py-10 text-center">
        <div className="mb-2 text-5xl">✓</div>
        <h1 className="mb-6 text-3xl text-brand">Walk-in recorded</h1>

        <div className="receipt card p-8">
          <div className="tabular mb-2 text-5xl font-bold text-brand">
            {formatEgp(done.amount_piastres, { hideZeroFraction: true })}
          </div>
          <div className="mb-4 text-lg">{classLabel(done.class_key)}</div>
          <dl className="space-y-1 border-t border-edge pt-4 text-sm text-white/60">
            <div>{formatDisplayDate(done.visited_at)}</div>
            <div>{PAYMENT_METHOD_LABELS[done.method]}</div>
            {done.person_name && <div dir="auto">{done.person_name}</div>}
          </dl>
        </div>

        <div className="no-print mt-6 flex gap-3">
          <button onClick={reset} className="btn flex-1">
            Another walk-in
          </button>
          <button onClick={() => window.print()} className="btn-ghost flex-1">
            Print
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-6">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-4xl text-brand">Walk-in</h1>
        <p className="text-sm text-white/40">
          Single session · {formatDisplayDate(todayISO())}
        </p>
      </div>

      <section className="card mb-6 p-6">
        <h2 className="mb-4 text-xl text-paper">Class</h2>
        <ClassPicker value={classKey} onChange={setClassKey} />
      </section>

      <section className="card mb-6 p-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-xl text-paper">Amount</h2>
            <p className="text-sm text-white/40">
              The standard walk-in rate. Only a manager can change it, in Settings or
              the management portal.
            </p>
          </div>
          <div className="tabular text-4xl font-bold text-brand">
            {formatEgp(price, { hideZeroFraction: true })}
          </div>
        </div>

        <div className="mt-4">
          <div className="label">Method</div>
          <div className="flex gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(m)}
                className={`min-h-touch flex-1 rounded-btn border-2 text-sm font-bold uppercase tracking-button transition-all duration-brand ${
                  method === m
                    ? 'border-brand bg-brand text-paper'
                    : 'border-edge bg-surface hover:border-brand'
                }`}
              >
                {PAYMENT_METHOD_LABELS[m]}
              </button>
            ))}
          </div>
        </div>

      </section>

      {/* Required, not optional: the academy needs a record of who trained. */}
      <section className="card mb-6 p-6">
        <h2 className="mb-4 text-xl text-paper">Who is training</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="label" htmlFor="wiName">
              Name
            </label>
            <input
              id="wiName"
              dir="auto"
              className="field"
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="Ahmed Hassan"
            />
            {touched && errors.personName && (
              <p className="mt-1 text-sm text-status-expired">{errors.personName}</p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="wiPhone">
              Phone
            </label>
            <input
              id="wiPhone"
              inputMode="tel"
              className="field tabular"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="01012345678"
            />
            {touched && errors.phone && (
              <p className="mt-1 text-sm text-status-expired">{errors.phone}</p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="wiBy">
              Received by
            </label>
            <input
              id="wiBy"
              className="field"
              value={receivedBy}
              onChange={(e) => setReceivedBy(e.target.value)}
              placeholder="Your name"
            />
          </div>
        </div>
      </section>

      {error && (
        <p className="mb-4 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
          {error}
        </p>
      )}

      <button onClick={save} disabled={saving} className="btn w-full text-lg">
        {saving ? 'Saving…' : `Take ${formatEgp(price, { hideZeroFraction: true })}`}
      </button>

      <section className="mt-10">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl text-paper">Today's walk-ins</h2>
          <Link to="/walk-ins" className="text-sm text-brand hover:underline">
            Full history →
          </Link>
        </div>
        {!recent ? (
          <p className="py-6 text-center text-sm text-white/25">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="py-6 text-center text-sm text-white/25">
            Nothing recorded yet today.
          </p>
        ) : (
          <ul className="space-y-2">
            {recent.map((row) => (
              <li
                key={row.id}
                className="card flex flex-wrap items-center gap-x-4 gap-y-2 p-4"
              >
                <div className="min-w-[160px] flex-1">
                  <div className="font-semibold" dir="auto">
                    {row.person_name}
                  </div>
                  <div className="text-sm text-white/40">
                    {classLabel(row.class_key)} · {formatDisplayDate(row.visited_at)}
                  </div>
                </div>
                <div className="tabular text-right text-sm font-bold text-brand">
                  {formatEgp(row.amount_piastres, { hideZeroFraction: true })}
                </div>
                <div className="min-w-[100px] text-right text-sm text-white/40">
                  {row.received_by}
                </div>
                <button
                  onClick={() => setVoidingFor(row)}
                  className="min-h-[40px] shrink-0 rounded-btn border border-status-expired/40 px-4
                             text-xs font-bold uppercase tracking-button text-status-expired
                             transition-all duration-brand hover:bg-status-expiredBg"
                >
                  Void
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {voidingFor && (
        <VoidWalkInDialog walkIn={voidingFor} onClose={() => setVoidingFor(null)} />
      )}
    </div>
  );
}
